/* eslint-disable  react/no-deprecated */

import React from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import { titleize } from 'underscore.string';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@cargomatic/ui';
import EquipmentForm from 'components/modules/equipment/EquipmentForm';
import EquipmentStore from 'stores/EquipmentStore';
import SessionStore from 'stores/SessionStore';
import EquipmentActionCreators from 'actions/EquipmentActionCreators';
import AuditHistory from 'components/modules/shipments/AuditHistory';
import { equipmentTypesMapping } from 'constants/Equipment';

class EquipmentEditModal extends React.Component {
  static propTypes = {
    show: PropTypes.bool.isRequired,
    equipment: PropTypes.object.isRequired,
    onHide: PropTypes.func.isRequired,
    onSave: PropTypes.func.isRequired,
  };

  constructor(props) {
    super(props);

    this.state = {
      equipment: props.equipment,
      audits: [],
      error: {},
      success: '',
      activeTab: 'Edit',
      isAdmin: SessionStore.isAdmin(),
    };
  }

  componentDidMount() {
    EquipmentStore.addEquipmentFormListener(this.handleStoreChange);
    EquipmentStore.addChangeListener(this.handleAuditsChange);
    if (this.props.equipment && this.props.equipment._id) {
      EquipmentActionCreators.getEquipmentAuditEvents(this.props.equipment._id);
    }
    SessionStore.addChangeListener(this.handleSessionStoreChange);
  }

  componentWillReceiveProps(props) {
    if (!this.props.show && props.show) {
      if (props.equipment && props.equipment._id) {
        EquipmentActionCreators.getEquipmentAuditEvents(props.equipment._id);
      }
      this.setState({
        equipment: props.equipment,
      });
    }
  }

  componentWillUnmount() {
    EquipmentStore.removeEquipmentFormListener(this.handleStoreChange);
    EquipmentStore.removeChangeListener(this.handleAuditsChange);
    SessionStore.removeChangeListener(this.handleSessionStoreChange);
  }

  handleSessionStoreChange = () => {
    const isAdmin = SessionStore.isAdmin();
    this.setState({
      isAdmin,
    });
  };

  handleAuditsChange = () => {
    let audits = [];

    if (this.state.equipment && this.state.equipment._id) {
      audits = EquipmentStore.getEquipmentAuditEvents(this.state.equipment._id);
    }

    this.setState({ audits });
  };

  handleStoreChange = () => {
    const { t } = this.props;
    const formStatus = EquipmentStore.getFormStatus();

    if (formStatus.success) {
      this.setState({
        error: {},
        success: t('EQUIPMENT_SUCCESSFULLY_UPDATED'),
      });
      setTimeout(() => this.handleHide(), 1500);
    } else {
      const error = { ...this.state.error };
      Object.keys(formStatus.error).forEach((key) => {
        error[key] = formStatus.error[key];
      });

      this.setState({
        error,
      });
    }
  };

  handleHide = () => {
    this.setState({
      equipment: null,
      error: {},
      success: {},
      activeTab: 'Edit',
    });
    this.props.onHide();
  };

  handleChange = (field, value) => {
    this.setState((currentState) => {
      const { equipment: currentEquipment } = currentState;

      const equipment = {
        ...currentEquipment,
        [field]: value,
      };

      /**
       * Reset chassis_owner_type and chassis_owner if is_private is set to false.
       */
      const fieldResetRequiredWhenToggleOff = field === 'is_private' && !value;
      if (fieldResetRequiredWhenToggleOff) {
        equipment.chassis_owner_type = null;
        equipment.chassis_owner = null;
      }

      /**
       * Below fields are not part of trailer and container,
       * so reset to default whenever _equipmentType changes
       */
      const fieldResetRequiredForNotChassisType =
        field === '_equipmentType' && value !== equipmentTypesMapping.CHASSIS;
      if (fieldResetRequiredForNotChassisType) {
        equipment.chassis_owner_type = null;
        equipment.chassis_owner = null;
        equipment.is_private = false;
      }
      /**
       * Below fields are not part of chassis and trailer,
       * so reset to default whenever _equipmentType changed to chassis or trailer
       */
      const fieldResetRequiredForNotContainer =
        field === '_equipmentType' && value !== equipmentTypesMapping.CONTAINER;
      if (fieldResetRequiredForNotContainer) {
        equipment._equipmentSize = null;
        equipment._type = null;
      }

      return {
        equipment,
      };
    });
  };

  handleSave = () => this.props.onSave(this.state.equipment);

  renderForm() {
    return (
      <EquipmentForm
        equipment={this.state.equipment}
        error={this.state.error}
        success={this.state.success}
        onFieldChange={this.handleChange}
        onSave={this.handleSave}
        onCancel={this.handleHide}
        isAdmin={this.state.isAdmin}
      />
    );
  }

  render() {
    const { t } = this.props;
    return (
      <Dialog
        open={this.props.show}
        onOpenChange={(open) => {
          if (!open) {
            this.handleHide();
          }
        }}
      >
        <DialogContent className="flex max-h-[85vh] flex-col gap-0 overflow-hidden p-0 sm:max-w-2xl">
          <DialogHeader className="shrink-0 border-b px-6 py-4">
            <DialogTitle>{titleize(t('EDIT_EQUIPMENT'))}</DialogTitle>
          </DialogHeader>
          {this.state.isAdmin ? (
            <Tabs
              value={this.state.activeTab}
              onValueChange={(value) => this.setState({ activeTab: value })}
              className="flex min-h-0 flex-1 flex-col gap-0"
            >
              <TabsList className="mx-6 mt-4 self-start">
                <TabsTrigger value="Edit">{titleize(t('EDIT'))}</TabsTrigger>
                <TabsTrigger value="Audit">{t('AUDIT_HISTORY')}</TabsTrigger>
              </TabsList>
              <TabsContent
                value="Edit"
                className="flex min-h-0 flex-1 flex-col"
              >
                {this.renderForm()}
              </TabsContent>
              <TabsContent
                value="Audit"
                className="min-h-0 flex-1 overflow-y-auto px-6 py-4"
              >
                <AuditHistory audits={this.state.audits} paginate />
              </TabsContent>
            </Tabs>
          ) : (
            this.renderForm()
          )}
        </DialogContent>
      </Dialog>
    );
  }
}

export default withTranslation()(EquipmentEditModal);
